import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, Column, Columns } from '@machobear/design-system'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionStorage from '@/hooks/use-sessionstorage'
import BuildingSearch from '@/components/building-search'
import { Button as OldButton } from '@/components/button-new'
import Error from '@/components/form/error'
import { ArrowRightIcon } from '@/components/icons'

import ClearButtonComponent from './clear-button'

const StyledOldButton = styled(OldButton)`
  height: 60px;
  @media (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

type Props = {
  promotionId: string
  placeholder: string
  buttonText: string
  trackingId: string
}

export const StandAloneBuildingSearch = ({
  promotionId,
  placeholder,
  buttonText,
  trackingId
}: Props) => {
  const [isBuildingSearchLoading, setIsBuildingSearchLoading] = useState(false)
  const router = useRouter()

  const [_, setAddress] = useSessionStorage(`${promotionId}_address`)
  const handleNavigation = () => {
    router.push(
      {
        pathname: '/post-a-listing',
        query: {
          accountType: 'growth',
          couponCode: promotionId,
          isAddressPrefilled: true
        }
      },
      `/post-a-listing?accountType=growth&couponCode=${promotionId}`
    )
  }
  const onSubmit = (values: any) => {
    setAddress?.(JSON.stringify(values))
    handleNavigation()
    window.dataLayer.push({
      event: trackingId
    })
  }

  const { t } = useTranslation()

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  const validationSchema = Yup.object().shape({
    street_name: Yup.string()
      .matches(
        /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
        'Must only contain numbers and letters'
      )
      .required(
        t('landlords:Listing address required!', 'Listing address required!')
      )
  })

  const setBuildingValues =
    ({ setFieldValue, validateForm }: any) =>
    (building: any) => {
      const {
        street_name: street_name,
        street_number: street_number,
        address: address,
        id: building_id,
        name: building_name,
        lat,
        lon,
        city,
        state,
        zip,
        isNew,
        ...rest
      } = building
      const values = {
        building,
        address,
        street_name,
        street_number: street_number || '',
        building_id,
        building_name,
        lat,
        lon,
        state: isNew ? '' : state,
        city: city || '',
        zip: zip || '',
        ...rest
      }
      Object.entries(values).forEach(([key, value]) =>
        setFieldValue(key, value)
      )
      validateForm(values)
    }
  const initialValues = {
    street_name: '',
    street_number: '',
    building: '',
    building_name: '',
    suite: '',
    city: '',
    state: '',
    zip: '',
    country: 'ca',
    type: ''
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({
        values,
        setFieldValue,
        setValues,
        validateForm,

        errors,
        handleSubmit
      }) => {
        const handleClear = () => {
          setValues({
            ...values,
            suite: '',
            building_name: '',
            city: '',
            state: '',
            zip: '',
            street_name: '',
            building: ''
          })
        }

        return (
          <Form>
            <Columns
              space="xxsmall"
              alignY={{ mobile: 'top', desktop: 'center' }}
              collapseBelow="tablet"
            >
              <Column>
                <Box position="relative">
                  <BuildingSearch
                    name="steet_name"
                    onChange={setBuildingValues({
                      setFieldValue,
                      validateForm
                    })}
                    isLoading={isBuildingSearchLoading}
                    setIsLoading={setIsBuildingSearchLoading}
                    handleClear={handleClear}
                    newAddressOnClick={handleNavigation}
                    value={values.building}
                    buildingType={values.type}
                    wrapBuildingName={isMobile}
                    countryCode={values.country}
                    placeholder={placeholder}
                    required
                    hasError={!!errors['street_name']}
                    ClearButtonComponent={ClearButtonComponent}
                    clearButton
                    showInputText={false}
                  />
                </Box>
              </Column>
              <Column width="content">
                <StyledOldButton
                  type="submit"
                  onClick={handleSubmit}
                  id={trackingId}
                >
                  <Box display="flex" alignItems="center" gap="xxsmall">
                    {buttonText}
                    <ArrowRightIcon />
                  </Box>
                </StyledOldButton>
              </Column>
            </Columns>
            <Error name={'street_name'} />
          </Form>
        )
      }}
    </Formik>
  )
}
